import {
  AutoComplete,
  FormContainer,
  FormInput,
  ISelectType,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createAutomationMessageTicketDefaulDoc,
  getAutomationMessageTicketDefaulDocById,
  updateAutomationMessageTicketDefaulDocById,
} from "../../../../../../../services/automationMessageTicketDefaulDoc.service";
import { listProtocolDefault } from "../../../../../../../services/protocolDefault.service";
import {
  automationMessageTicketDefaulDocSchema,
  TAutomationMessageTicketDefaulDocForm,
} from "./automationMessageTicketDefaulDocSchema";
import { IAutomationMessageTicketDefaulDocProps } from "./models";
import { SchedulesField } from "./SchedulesField";
import { InsurancesField } from "./InsurancesField";
import { InternalProceduresField } from "./InternalProceduresField";
import { useAutomationRules } from "../../../../../../../store/contexts/AutomationRulesContext";
import MainApi from "../../../../../../../services/mainApi.service";

const translationPath =
  "chatBot.page.automations.automationMessageTicketDefaulDoc.";

const GENERIC_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";

const genericService = new MainApi<ISelectType>(GENERIC_ROUTE);

export const AutomationMessageTicketDefaulDoc = memo(
  ({
    id,
    ticketAutomationRuleId,
    onBackButton,
  }: IAutomationMessageTicketDefaulDocProps) => {
    const { t } = useTranslation();

    const { automation } = useAutomationRules();

    const [options, setOptions] = useState<ISelectType[]>([]);

    const { sendRequest } = useFetch(genericService.list);

    const fetchGenerics = useCallback(async () => {
      if (!automation) return;
      const { data, success } = await sendRequest({
        Identifier: "TicketAutomationMessageProtocolSendWhen",
      });

      let filtered: string[] = [];
      if (automation.code === "7") {
        filtered = ["4"];
      } else if (["1", "5", "11"].includes(String(automation.code))) {
        filtered = ["1", "2", "3", "5", "6"];
      } else if (
        ["12", "13", "14", "16", "17", "18", "22"].includes(
          String(automation.code)
        )
      ) {
        filtered = ["1", "3", "5", "6"];
      } else if (["23"].includes(String(automation.code))) {
        filtered = ["1", "3"];
      } else if (["24"].includes(String(automation.code))) {
        filtered = ["1", "3", "7"];
      } else {
        filtered = ["1", "2", "3"];
      }

      if (data && success) {
        setOptions(
          data
            .filter((d) => filtered.includes(String(d.code)))
            .map((d) => ({
              id: d.id,
              name: d.name,
            }))
        );
      }
    }, [sendRequest, automation]);

    useEffect(() => {
      fetchGenerics();
    }, [fetchGenerics]);

    const { sendRequest: save, loading: sLoading } = useFetch(
      createAutomationMessageTicketDefaulDoc
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateAutomationMessageTicketDefaulDocById
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getAutomationMessageTicketDefaulDocById
    );

    const updateHandler = (
      v: TAutomationMessageTicketDefaulDocForm,
      id: string
    ) =>
      update({
        item: {
          id: id,
          ticketAutomationRuleId: ticketAutomationRuleId ?? "",
          enable: v.enable,
          description: v.description ? v.description : undefined,
          schedules: v.schedules ? v.schedules : undefined,
          insurances: v.insurances ? v.insurances : undefined,
          internalProcedures: v.internalProcedures
            ? v.internalProcedures
            : undefined,
          sendWhenId: v.sendWhen.id,
          ticketDefaultDocId: v.ticketDefaultDoc.id,
        },
        id: id,
      });

    const saveHandler = (v: TAutomationMessageTicketDefaulDocForm) =>
      save({
        ticketAutomationRuleId: ticketAutomationRuleId ?? "",
        enable: v.enable,
        description: v.description ? v.description : undefined,
        schedules: v.schedules ? v.schedules : undefined,
        insurances: v.insurances ? v.insurances : undefined,
        internalProcedures: v.internalProcedures
          ? v.internalProcedures
          : undefined,
        sendWhenId: v.sendWhen.id,
        ticketDefaultDocId: v.ticketDefaultDoc.id,
      });

    const getHandler = useCallback(
      async (
        id: string
      ): Promise<Treturn<TAutomationMessageTicketDefaulDocForm>> => {
        const { data, success } = await get(id);

        if (data) {
          const newData: TAutomationMessageTicketDefaulDocForm = {
            ...data,
            ticketDefaultDoc: data.ticketDefaultDoc,
            enable: data.enable,
            description: data.description,
            schedules: data.schedules,
            insurances: data.insurances,
            internalProcedures: data.internalProcedures,
            sendWhen: data.sendWhen,
          };
          return { data: newData, success: true };
        }
        return { data, success };
      },
      [get]
    );
    const successHandle = () => {
      onBackButton();
    };
    const loading = sLoading || uLoading || gLoading;
    return (
      <FormContainer<TAutomationMessageTicketDefaulDocForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "title")}
        schema={automationMessageTicketDefaulDocSchema}
        subtitle={t(translationPath + "new")}
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={successHandle}
        isModalForm={true}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item sm={12} textAlign={"end"}>
              <SwitchInput
                name="enable"
                label={t(translationPath + "enable")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <AutoComplete
                getOptionLabel={({ name }) => name}
                size="small"
                label={t(translationPath + "send_when")}
                name="sendWhen"
                options={options}
                clearIcon={false}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <AutoComplete
                getOptionLabel={({ name }) => name}
                params={{ TicketAutomationId: automation?.id }}
                size="small"
                label={t(translationPath + "ticket_default_doc")}
                name="ticketDefaultDoc"
                request={listProtocolDefault}
                clearIcon={false}
              />
            </Grid>
            <SchedulesField />
            <InsurancesField />
            <InternalProceduresField />
            <Grid item xs={12}>
              <FormInput
                size="small"
                multiline
                name="description"
                fullWidth
                minRows={4}
                label={t(translationPath + "description")}
              />
            </Grid>
          </Grid>
        )}
      />
    );
  }
);
